import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Search,
  DollarSign,
  FileText,
  Trash2,
  Edit,
  X,
  Loader2,
  User,
  Mail,
  Phone,
  Building,
  AlertTriangle,
  Check,
  Calendar,
  CreditCard
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const emptyContractor = {
  name: '',
  business_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  ein_ssn: '',
  ein_ssn_type: 'ssn',
  w9_received: false,
  notes: ''
};

const emptyPayment = {
  amount: '',
  payment_date: new Date().toISOString().split('T')[0],
  payment_method: 'check',
  check_number: '',
  description: ''
};

export default function Contractors() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [loading, setLoading] = useState(true);
  const [contractors, setContractors] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [formData, setFormData] = useState(emptyContractor);
  const [paymentData, setPaymentData] = useState(emptyPayment);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const fetchContractors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/business/contractors`, {
        params: { year: selectedYear },
        withCredentials: true
      });
      setContractors(response.data.contractors || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  const handleCreateNew = () => {
    setEditingContractor(null);
    setFormData(emptyContractor);
    setShowForm(true);
  };

  const handleEdit = (contractor) => {
    setEditingContractor(contractor);
    setFormData({
      name: contractor.name || '',
      business_name: contractor.business_name || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      address: contractor.address || '',
      city: contractor.city || '',
      state: contractor.state || '',
      zip_code: contractor.zip_code || '',
      ein_ssn: '',
      ein_ssn_type: contractor.ein_ssn_type || 'ssn',
      w9_received: contractor.w9_received || false,
      notes: contractor.notes || ''
    });
    setShowForm(true);
    setSelectedContractor(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!formData.name) {
      toast.error('Contractor name is required');
      return;
    }

    try {
      setSaving(true);
      
      const dataToSend = {...formData};
      if (!dataToSend.ein_ssn) delete dataToSend.ein_ssn;
      
      if (editingContractor) {
        await axios.put(`${API_URL}/api/business/contractors/${editingContractor.id}`, dataToSend, {
          withCredentials: true
        });
        toast.success('Contractor updated');
      } else {
        await axios.post(`${API_URL}/api/business/contractors`, dataToSend, {
          withCredentials: true
        });
        toast.success('Contractor created');
      }
      
      setShowForm(false);
      setFormData(emptyContractor);
      setEditingContractor(null);
      fetchContractors();
    } catch (error) {
      toast.error('Failed to save contractor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contractorId) => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!window.confirm('Are you sure you want to delete this contractor?')) return;

    try {
      await axios.delete(`${API_URL}/api/business/contractors/${contractorId}`, {
        withCredentials: true
      });
      toast.success('Contractor deleted');
      fetchContractors();
      setSelectedContractor(null);
    } catch (error) {
      toast.error('Failed to delete contractor');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/business/contractors/${selectedContractor.id}/payments`, {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      }, { withCredentials: true });
      
      toast.success('Payment recorded');
      setShowPaymentForm(false);
      setPaymentData(emptyPayment);
      fetchContractors();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const filteredContractors = contractors.filter(c => {
    if (activeTab === 'requires-1099') return c.requires_1099;
    if (activeTab === 'missing-w9') return !c.w9_received;
    return true;
  });

  if (loading && contractors.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="contractors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">1099 Contractors</h1>
          <p className="text-muted-foreground mt-1">
            Track payments and generate 1099-NEC forms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg border bg-background"
          >
            {[2024, 2025, 2026, 2027].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <Button onClick={handleCreateNew} data-testid="add-contractor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Contractor
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Contractors</p>
                <p className="text-2xl font-bold">{summary.total_contractors || 0}</p>
              </div>
              <Users className="w-8 h-8 text-muted-foreground/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid (YTD)</p>
                <p className="text-2xl font-bold text-blue-500">
                  {formatCurrency(summary.total_paid_ytd || 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Require 1099</p>
                <p className="text-2xl font-bold text-amber-500">{summary.requiring_1099 || 0}</p>
                <p className="text-xs text-muted-foreground">≥ $600 paid</p>
              </div>
              <FileText className="w-8 h-8 text-amber-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">1099 Threshold</p>
                <p className="text-2xl font-bold">${summary.threshold || 600}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-muted-foreground/40" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contractor Form */}
      {showForm && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {editingContractor ? 'Edit Contractor' : 'New Contractor'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Contractor Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name (DBA)</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                    placeholder="Smith Consulting LLC"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">ZIP</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ein_ssn_type">Tax ID Type</Label>
                  <select
                    id="ein_ssn_type"
                    value={formData.ein_ssn_type}
                    onChange={(e) => setFormData({...formData, ein_ssn_type: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border bg-background"
                  >
                    <option value="ssn">SSN</option>
                    <option value="ein">EIN</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ein_ssn">
                    {formData.ein_ssn_type === 'ein' ? 'EIN' : 'SSN'} (for 1099)
                  </Label>
                  <Input
                    id="ein_ssn"
                    value={formData.ein_ssn}
                    onChange={(e) => setFormData({...formData, ein_ssn: e.target.value})}
                    placeholder={formData.ein_ssn_type === 'ein' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX'}
                  />
                  <p className="text-xs text-muted-foreground">Stored securely, masked in display</p>
                </div>
                <div className="space-y-2">
                  <Label>W-9 Received?</Label>
                  <div className="flex items-center gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.w9_received}
                        onChange={(e) => setFormData({...formData, w9_received: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Yes, W-9 on file</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingContractor ? 'Update' : 'Add Contractor'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Payment Form */}
      {showPaymentForm && selectedContractor && (
        <Card className="border-emerald-500/20 bg-emerald-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Record Payment to {selectedContractor.name}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowPaymentForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_date">Date</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Method</Label>
                  <select
                    id="payment_method"
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border bg-background"
                  >
                    <option value="check">Check</option>
                    <option value="ach">ACH/Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="check_number">Check #</Label>
                  <Input
                    id="check_number"
                    value={paymentData.check_number}
                    onChange={(e) => setPaymentData({...paymentData, check_number: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={paymentData.description}
                  onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
                  placeholder="e.g., Web development - January"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowPaymentForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Record Payment
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tabs & List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({contractors.length})</TabsTrigger>
          <TabsTrigger value="requires-1099">
            Require 1099 ({contractors.filter(c => c.requires_1099).length})
          </TabsTrigger>
          <TabsTrigger value="missing-w9">
            Missing W-9 ({contractors.filter(c => !c.w9_received).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredContractors.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium mb-2">No contractors found</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contractor
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredContractors.map((contractor) => {
                const progressPercent = Math.min(100, (contractor.total_paid_ytd / 600) * 100);
                
                return (
                  <Card
                    key={contractor.id}
                    className={`cursor-pointer hover:border-primary/30 transition-colors ${
                      selectedContractor?.id === contractor.id ? 'border-primary' : ''
                    }`}
                    onClick={() => setSelectedContractor(selectedContractor?.id === contractor.id ? null : contractor)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            contractor.requires_1099 ? 'bg-amber-500/10' : 'bg-primary/10'
                          }`}>
                            <User className={`w-6 h-6 ${contractor.requires_1099 ? 'text-amber-500' : 'text-primary'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{contractor.name}</p>
                              {contractor.requires_1099 && (
                                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                                  1099 Required
                                </Badge>
                              )}
                              {!contractor.w9_received && (
                                <Badge variant="outline" className="text-red-500 border-red-500/30">
                                  No W-9
                                </Badge>
                              )}
                            </div>
                            {contractor.business_name && (
                              <p className="text-sm text-muted-foreground">{contractor.business_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">
                            {formatCurrency(contractor.total_paid_ytd || 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {contractor.payment_count || 0} payment{contractor.payment_count !== 1 ? 's' : ''} in {selectedYear}
                          </p>
                        </div>
                      </div>

                      {/* Progress to 1099 threshold */}
                      {!contractor.requires_1099 && contractor.total_paid_ytd > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>Progress to 1099 threshold</span>
                            <span>{formatCurrency(contractor.total_paid_ytd)} / $600</span>
                          </div>
                          <Progress value={progressPercent} className="h-1.5" />
                        </div>
                      )}

                      {/* Expanded View */}
                      {selectedContractor?.id === contractor.id && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {contractor.email && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Mail className="w-4 h-4" />
                                {contractor.email}
                              </div>
                            )}
                            {contractor.phone && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                {contractor.phone}
                              </div>
                            )}
                            {contractor.ein_ssn_masked && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <FileText className="w-4 h-4" />
                                {contractor.ein_ssn_type?.toUpperCase()}: {contractor.ein_ssn_masked}
                              </div>
                            )}
                            {contractor.w9_received && (
                              <div className="flex items-center gap-2 text-emerald-500">
                                <Check className="w-4 h-4" />
                                W-9 on file
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <Button 
                              size="sm"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setPaymentData(emptyPayment);
                                setShowPaymentForm(true);
                              }}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Record Payment
                            </Button>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(contractor); }}>
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-500"
                              onClick={(e) => { e.stopPropagation(); handleDelete(contractor.id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
